import React, { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { Sparkles, Check, Loader2 } from 'lucide-react';

const PROGRESS_STEPS = [
    { id: 1, label: "Analyzing your idea...", duration: 2000 },
    { id: 2, label: "Planning features...", duration: 3000 },
    { id: 3, label: "Designing screens...", duration: 3000 },
    { id: 4, label: "Structuring database...", duration: 3000 },
    { id: 5, label: "Generating API routes...", duration: 3000 },
    { id: 6, label: "Creating build plan...", duration: 4000 },
];

export const LoadingState = () => {
    const [currentStep, setCurrentStep] = useState(0);
    const [completedSteps, setCompletedSteps] = useState([]);

    useEffect(() => {
        let timeoutId;
        
        const advanceStep = () => {
            if (currentStep < PROGRESS_STEPS.length - 1) {
                setCompletedSteps(prev => [...prev, currentStep]);
                setCurrentStep(prev => prev + 1);
                timeoutId = setTimeout(advanceStep, PROGRESS_STEPS[currentStep + 1]?.duration || 3000);
            }
        };

        timeoutId = setTimeout(advanceStep, PROGRESS_STEPS[0].duration);
        
        return () => clearTimeout(timeoutId);
    }, [currentStep]);

    return (
        <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            className="glass-card p-8 md:p-12"
            data-testid="loading-state"
        >
            <div className="flex flex-col md:flex-row gap-8 items-start">
                {/* Left side - Animation */}
                <div className="flex-shrink-0 mx-auto md:mx-0">
                    <motion.div
                        animate={{ 
                            scale: [1, 1.1, 1],
                            rotate: [0, 5, -5, 0]
                        }}
                        transition={{ 
                            duration: 2, 
                            repeat: Infinity,
                            ease: "easeInOut"
                        }}
                        className="w-20 h-20 rounded-2xl bg-primary/20 flex items-center justify-center animate-pulse-glow"
                    >
                        <Sparkles className="w-10 h-10 text-primary" />
                    </motion.div>
                </div>

                {/* Right side - Progress Steps */}
                <div className="flex-1 w-full">
                    <h3 className="font-heading text-xl md:text-2xl font-medium text-white mb-2">
                        Building Your Blueprint
                    </h3>
                    <p className="text-zinc-500 text-sm mb-6">
                        This usually takes 12-20 seconds
                    </p>

                    <div className="space-y-3">
                        {PROGRESS_STEPS.map((step, index) => {
                            const isCompleted = completedSteps.includes(index);
                            const isCurrent = currentStep === index;
                            const isPending = index > currentStep;

                            return (
                                <motion.div
                                    key={step.id}
                                    initial={{ opacity: 0, x: -20 }}
                                    animate={{ opacity: 1, x: 0 }}
                                    transition={{ delay: index * 0.1 }}
                                    className={`flex items-center gap-3 py-2 px-3 rounded-lg transition-all duration-300 ${
                                        isCurrent ? 'bg-primary/10 border border-primary/30' :
                                        isCompleted ? 'bg-green-500/5' : 'opacity-50'
                                    }`}
                                    data-testid={`progress-step-${step.id}`}
                                >
                                    <div className={`w-6 h-6 rounded-full flex items-center justify-center flex-shrink-0 ${
                                        isCompleted ? 'bg-green-500/20' :
                                        isCurrent ? 'bg-primary/20' : 'bg-zinc-800'
                                    }`}>
                                        <AnimatePresence mode="wait">
                                            {isCompleted ? (
                                                <motion.div
                                                    key="check"
                                                    initial={{ scale: 0 }}
                                                    animate={{ scale: 1 }}
                                                    exit={{ scale: 0 }}
                                                >
                                                    <Check className="w-4 h-4 text-green-400" />
                                                </motion.div>
                                            ) : isCurrent ? (
                                                <motion.div
                                                    key="loading"
                                                    animate={{ rotate: 360 }}
                                                    transition={{ duration: 1, repeat: Infinity, ease: "linear" }}
                                                >
                                                    <Loader2 className="w-4 h-4 text-primary" />
                                                </motion.div>
                                            ) : (
                                                <span className="text-xs text-zinc-500">{index + 1}</span>
                                            )}
                                        </AnimatePresence>
                                    </div>
                                    <span className={`text-sm ${
                                        isCompleted ? 'text-green-400' :
                                        isCurrent ? 'text-white font-medium' : 'text-zinc-500'
                                    }`}>
                                        {step.label}
                                    </span>
                                </motion.div>
                            );
                        })}
                    </div>

                    {/* Progress bar */}
                    <div className="mt-6 h-1.5 bg-zinc-800 rounded-full overflow-hidden">
                        <motion.div
                            className="h-full bg-gradient-to-r from-violet-500 to-fuchsia-500"
                            initial={{ width: '0%' }}
                            animate={{ width: `${((currentStep + 1) / PROGRESS_STEPS.length) * 100}%` }}
                            transition={{ duration: 0.5, ease: "easeOut" }}
                        />
                    </div>
                </div>
            </div>
        </motion.div>
    );
};
